class BcHeroSlider {
  constructor(root) {
    this.root         = root;
    this.viewport     = root.querySelector('.bc-hero-slider__viewport');
    this.track        = root.querySelector('.bc-hero-slider__track');
    this.slides       = Array.from(root.querySelectorAll('[data-slide]'));
    this.realSlides   = Array.from(root.querySelectorAll('[data-real-slide="true"]'));
    this.dots         = Array.from(root.querySelectorAll('.bc-hero-slider__dot'));
    this.prevBtn      = root.querySelector('.bc-hero-slider__arrow--prev');
    this.nextBtn      = root.querySelector('.bc-hero-slider__arrow--next');
    this.realTotal    = this.realSlides.length;
    this.current      = this.realTotal > 1 ? 1 : 0;
    this.slideWidth   = root.offsetWidth;
    this.autoplay     = root.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(root.dataset.autoplaySpeed, 10) || 5000;
    this.dragThreshold = 0.15;
    this.dragOffset   = 0;
    this.pointerId    = null;
    this.startX       = 0;
    this.isDragging      = false;
    this.isTransitioning = false;
    this.transitionTimer = null;  // fallback if transitionend never fires
    this.timer           = null;

    if (!this.viewport || !this.track || this.realTotal === 0) return;

    this.imagesHydrated = false;

    this.setPosition(false);
    this.bindEvents();
    this.scheduleImageHydration();

    if (this.autoplay && this.realTotal > 1) this.startAutoplay();
  }

  // Slides past the first ship their URLs in data-* attributes so the browser
  // cannot request them while the LCP image is still loading. Swapping the
  // attributes in is what actually starts those downloads.
  hydrateImages() {
    if (this.imagesHydrated) return;
    this.imagesHydrated = true;

    this.root.querySelectorAll('picture[data-bc-deferred]').forEach((picture) => {
      picture.querySelectorAll('source[data-srcset]').forEach((source) => {
        source.srcset = source.dataset.srcset;
        source.removeAttribute('data-srcset');
      });

      const img = picture.querySelector('img[data-src]');
      if (img) {
        // srcset first: setting src alone would briefly commit the browser to
        // the placeholder before the candidate list exists.
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        img.src = img.dataset.src;
        img.removeAttribute('data-srcset');
        img.removeAttribute('data-src');
      }

      picture.removeAttribute('data-bc-deferred');
    });
  }

  scheduleImageHydration() {
    const hydrate = () => this.hydrateImages();

    // Normal path: once the page has finished loading, fill the idle gap.
    const whenIdle = () => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(hydrate, { timeout: 2000 });
      } else {
        window.setTimeout(hydrate, 200);
      }
    };

    if (document.readyState === 'complete') {
      whenIdle();
    } else {
      window.addEventListener('load', whenIdle, { once: true });
    }

    // Impatient path: any sign the visitor is about to move the slider wins
    // over the idle timer, so a swipe never lands on a blank slide.
    ['pointerdown', 'keydown', 'mouseenter', 'focusin'].forEach((evt) => {
      this.root.addEventListener(evt, hydrate, { once: true, passive: true });
    });
  }

  bindEvents() {
    if (this.realTotal > 1) {
      this.prevBtn?.addEventListener('click', () => { this.prev(); this.restartAutoplay(); });
      this.nextBtn?.addEventListener('click', () => { this.next(); this.restartAutoplay(); });

      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => { this.goTo(i); this.restartAutoplay(); });
      });

      // Guard against bubbled transitionend events from child elements (buttons,
      // links, etc. all have Dawn base transitions). Only act on the track's own
      // transform transition; anything else would corrupt the clone-jump logic.
      this.track.addEventListener('transitionend', (e) => {
        if (e.target !== this.track) return;
        if (e.propertyName !== 'transform') return;
        this.onTransitionEnd();
      });
      this.viewport.addEventListener('pointerdown',    (e) => this.onPointerDown(e));
      this.viewport.addEventListener('pointermove',    (e) => this.onPointerMove(e));
      this.viewport.addEventListener('pointerup',      (e) => this.onPointerUp(e));
      this.viewport.addEventListener('pointercancel',  (e) => this.onPointerUp(e));
      this.viewport.addEventListener('lostpointercapture', (e) => this.onPointerUp(e));
    }

    this.root.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft')  this.prev();
      if (e.key === 'ArrowRight') this.next();
    });

    if (this.autoplay && this.realTotal > 1) {
      this.root.addEventListener('mouseenter', () => this.stopAutoplay());
      this.root.addEventListener('mouseleave', () => this.startAutoplay());
      this.root.addEventListener('focusin',    () => this.stopAutoplay());
      this.root.addEventListener('focusout',   () => this.startAutoplay());
    }

    let _resizeTimer = 0;
    window.addEventListener('resize', () => {
      clearTimeout(_resizeTimer);
      _resizeTimer = setTimeout(() => {
        this.slideWidth = this.root.offsetWidth;
        this.setPosition(false);
      }, 150);
    });
  }

  onPointerDown(e) {
    if (this.realTotal <= 1) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (e.target.closest('.bc-button, .bc-hero-slider__arrow, .bc-hero-slider__dot')) return;

    this.isDragging = true;
    this.pointerId  = e.pointerId;
    this.startX     = e.clientX;
    this.dragOffset = 0;
    this.slideWidth = this.root.offsetWidth;
    this.root.classList.add('is-dragging');
    this.viewport.setPointerCapture(e.pointerId);
    this.stopAutoplay();
  }

  onPointerMove(e) {
    if (!this.isDragging || e.pointerId !== this.pointerId) return;
    this.dragOffset = e.clientX - this.startX;
    this.setPosition(false, this.dragOffset);
  }

  onPointerUp(e) {
    if (!this.isDragging) return;
    if (this.pointerId !== null && e.pointerId !== this.pointerId) return;

    this.isDragging = false;
    this.root.classList.remove('is-dragging');

    const ratio = Math.abs(this.dragOffset) / this.slideWidth;
    if (ratio > this.dragThreshold) {
      this.dragOffset < 0 ? this.next() : this.prev();
    } else {
      this.setPosition(true);
    }

    this.pointerId  = null;
    this.dragOffset = 0;
    this.restartAutoplay();
  }

  realIndex() {
    if (this.realTotal <= 1) return 0;
    if (this.current === 0) return this.realTotal - 1;
    if (this.current === this.slides.length - 1) return 0;
    return this.current - 1;
  }

  goTo(index) {
    if (this.realTotal <= 1) return;
    this.current = index + 1;
    this.update(true);
  }

  next() {
    if (this.realTotal <= 1 || this.isTransitioning) return;
    this.current += 1;
    this.update(true);
  }

  prev() {
    if (this.realTotal <= 1 || this.isTransitioning) return;
    this.current -= 1;
    this.update(true);
  }

  update(animate) {
    // Covers autoplay and every programmatic path: nothing may slide into view
    // before its image has been handed a real URL.
    this.hydrateImages();

    this.isTransitioning = animate && this.realTotal > 1;
    this.setPosition(animate);
    this.syncUi();

    // Fallback: if transitionend never fires (tab hidden, cancelled, etc.)
    // unlock the slider after transition duration + buffer.
    if (this.isTransitioning) {
      clearTimeout(this.transitionTimer);
      this.transitionTimer = setTimeout(() => this.onTransitionEnd(), 700);
    }
  }

  setPosition(animate, offset = 0) {
    const x = this.current * this.slideWidth * -1 + offset;
    this.track.classList.toggle('is-instant', !animate);
    this.track.style.transform = `translate3d(${x}px, 0, 0)`;
  }

  syncUi() {
    const active = this.realIndex();

    this.realSlides.forEach((slide, i) => {
      const isActive = i === active;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
    });

    this.slides.forEach((slide) => {
      if (slide.dataset.realSlide !== 'true') slide.setAttribute('aria-hidden', 'true');
    });

    this.dots.forEach((dot, i) => {
      const isActive = i === active;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', String(isActive));
    });
  }

  onTransitionEnd() {
    clearTimeout(this.transitionTimer);
    if (this.realTotal <= 1) return;

    if (this.current === 0) {
      this.current = this.realTotal;
      this.setPosition(false);
    } else if (this.current === this.slides.length - 1) {
      this.current = 1;
      this.setPosition(false);
    }

    this.isTransitioning = false;
    this.syncUi();
  }

  startAutoplay() {
    if (!this.autoplay || this.realTotal <= 1) return;
    this.stopAutoplay();
    this.timer = window.setInterval(() => this.next(), this.autoplaySpeed);
  }

  stopAutoplay() {
    window.clearInterval(this.timer);
    this.timer = null;
  }

  restartAutoplay() {
    if (this.autoplay && this.realTotal > 1) this.startAutoplay();
  }
}

function initBcHeroSliders() {
  document.querySelectorAll('.bc-hero-slider').forEach((el) => {
    if (el.dataset.bcSliderInit === 'true') return;
    el.dataset.bcSliderInit = 'true';
    new BcHeroSlider(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBcHeroSliders);
} else {
  initBcHeroSliders();
}

document.addEventListener('shopify:section:load', (e) => {
  const slider = e.target.querySelector('.bc-hero-slider');
  if (!slider) return;
  slider.dataset.bcSliderInit = 'false';
  initBcHeroSliders();
});
